import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';
import { submitPrivacyRequest } from '../../services/privacyRequestService';

const REQUEST_TYPES = [
    { value: 'Delete my personal data', label: 'Delete my personal data' },
    { value: 'Access my personal data', label: 'Access my personal data' },
    { value: 'Correct my personal data', label: 'Correct my personal data' },
    { value: 'Export my personal data (portability)', label: 'Export my personal data (portability)' }
];

const REQUESTOR_TYPES = [
    {
        value: 'A SalesPinch platform user (I have an account)',
        label: 'A SalesPinch platform user (I have an account)'
    },
    {
        value: 'A person whose data may have been captured (e.g. via Instagram or a lead list)',
        label: 'A person whose data may have been captured (e.g. via Instagram or a lead list)'
    },
    {
        value: 'Representing a business / client account',
        label: 'Representing a business / client account'
    }
];

const NOTES_MAX = 1000;

const initialForm = {
    name: '',
    email: '',
    requestType: '',
    requestorType: '',
    notes: '',
    confirmAuthorized: false
};

function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

const PrivacyRequestPage = () => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [successEmail, setSuccessEmail] = useState(null);

    const setField = (name, value) => {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
        setApiError(null);
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = 'Full name is required.';
        if (!form.email.trim()) {
            next.email = 'Email address is required.';
        } else if (!validateEmail(form.email)) {
            next.email = 'Enter a valid email address.';
        }
        if (!form.requestType) next.requestType = 'Please select a request type.';
        if (!form.requestorType) next.requestorType = 'Please select who you are.';
        if (form.notes.length > NOTES_MAX) {
            next.notes = `Additional context must be ${NOTES_MAX} characters or fewer.`;
        }
        if (!form.confirmAuthorized) {
            next.confirmAuthorized = 'You must confirm this statement to continue.';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setApiError(null);
        if (!validate()) return;

        setSubmitting(true);
        const payload = {
            name: form.name.trim(),
            email: form.email.trim(),
            requestType: form.requestType,
            requestorType: form.requestorType,
            notes: form.notes.trim() || undefined
        };

        const result = await submitPrivacyRequest(payload);
        setSubmitting(false);

        if (result.ok) {
            setSuccessEmail(form.email.trim());
            setForm(initialForm);
            setErrors({});
        } else {
            setApiError(result.message || 'Something went wrong. Please try again later.');
        }
    };

    return (
        <LegalPageLayout>
            <div className="legal-page">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8">
                            <nav aria-label="breadcrumb" className="mt-4">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Privacy &amp; Data Request
                                    </li>
                                </ol>
                            </nav>

                            <div className="legal-content privacy-request-page">
                                <h1 className="h2 font-weight-bold text-center mb-3">Privacy &amp; Data Request</h1>
                                <p className="lead text-muted text-center mb-4">
                                    Submit a request to access, correct, export, or delete your personal data held by
                                    SalesPinch. We will verify your identity before processing any request.
                                </p>

                                {successEmail && (
                                    <div
                                        className="alert alert-success"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        We&apos;ve sent a verification link to {successEmail}. Please check your inbox
                                        and click the link to confirm your request. The link expires in 24 hours.
                                    </div>
                                )}

                                {apiError && (
                                    <div className="alert alert-danger" role="alert" aria-live="assertive">
                                        {apiError}
                                    </div>
                                )}

                                <form onSubmit={onSubmit} noValidate>
                                    <div className="form-group">
                                        <label htmlFor="privacy-request-name">Full Name</label>
                                        <input
                                            id="privacy-request-name"
                                            name="name"
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            value={form.name}
                                            onChange={(e) => setField('name', e.target.value)}
                                            disabled={submitting}
                                            autoComplete="name"
                                            aria-required="true"
                                            aria-invalid={!!errors.name}
                                            aria-describedby={errors.name ? 'privacy-request-name-error' : undefined}
                                        />
                                        {errors.name && (
                                            <div id="privacy-request-name-error" className="invalid-feedback d-block">
                                                {errors.name}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="privacy-request-email">Email Address</label>
                                        <input
                                            id="privacy-request-email"
                                            name="email"
                                            type="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            value={form.email}
                                            onChange={(e) => setField('email', e.target.value)}
                                            disabled={submitting}
                                            autoComplete="email"
                                            aria-required="true"
                                            aria-invalid={!!errors.email}
                                            aria-describedby={errors.email ? 'privacy-request-email-error' : undefined}
                                        />
                                        {errors.email && (
                                            <div id="privacy-request-email-error" className="invalid-feedback d-block">
                                                {errors.email}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="privacy-request-type">Request Type</label>
                                        <select
                                            id="privacy-request-type"
                                            name="requestType"
                                            className={`form-control ${errors.requestType ? 'is-invalid' : ''}`}
                                            value={form.requestType}
                                            onChange={(e) => setField('requestType', e.target.value)}
                                            disabled={submitting}
                                            aria-required="true"
                                            aria-invalid={!!errors.requestType}
                                            aria-describedby={
                                                errors.requestType ? 'privacy-request-type-error' : undefined
                                            }
                                        >
                                            <option value="">Select a request type</option>
                                            {REQUEST_TYPES.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.requestType && (
                                            <div id="privacy-request-type-error" className="invalid-feedback d-block">
                                                {errors.requestType}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="privacy-requestor-type">I am a...</label>
                                        <select
                                            id="privacy-requestor-type"
                                            name="requestorType"
                                            className={`form-control ${errors.requestorType ? 'is-invalid' : ''}`}
                                            value={form.requestorType}
                                            onChange={(e) => setField('requestorType', e.target.value)}
                                            disabled={submitting}
                                            aria-required="true"
                                            aria-invalid={!!errors.requestorType}
                                            aria-describedby={
                                                errors.requestorType ? 'privacy-requestor-type-error' : undefined
                                            }
                                        >
                                            <option value="">Select an option</option>
                                            {REQUESTOR_TYPES.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.requestorType && (
                                            <div
                                                id="privacy-requestor-type-error"
                                                className="invalid-feedback d-block"
                                            >
                                                {errors.requestorType}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="privacy-request-notes">
                                            Describe your request or any details that will help us locate your data
                                            (optional)
                                        </label>
                                        <textarea
                                            id="privacy-request-notes"
                                            name="notes"
                                            className={`form-control ${errors.notes ? 'is-invalid' : ''}`}
                                            rows={5}
                                            maxLength={NOTES_MAX}
                                            value={form.notes}
                                            onChange={(e) => setField('notes', e.target.value)}
                                            disabled={submitting}
                                            aria-describedby="privacy-request-notes-count"
                                        />
                                        <small id="privacy-request-notes-count" className="form-text text-muted">
                                            {form.notes.length} / {NOTES_MAX} characters
                                        </small>
                                        {errors.notes && (
                                            <div className="invalid-feedback d-block">{errors.notes}</div>
                                        )}
                                    </div>

                                    <div className="form-group form-check">
                                        <input
                                            id="privacy-request-confirm"
                                            name="confirmAuthorized"
                                            type="checkbox"
                                            className={`form-check-input ${errors.confirmAuthorized ? 'is-invalid' : ''}`}
                                            checked={form.confirmAuthorized}
                                            onChange={(e) => setField('confirmAuthorized', e.target.checked)}
                                            disabled={submitting}
                                            aria-required="true"
                                            aria-invalid={!!errors.confirmAuthorized}
                                        />
                                        <label className="form-check-label" htmlFor="privacy-request-confirm">
                                            I confirm I am the person named above or authorised to act on their behalf
                                        </label>
                                        {errors.confirmAuthorized && (
                                            <div className="invalid-feedback d-block">{errors.confirmAuthorized}</div>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg btn-block mt-3 d-inline-flex align-items-center justify-content-center"
                                        disabled={submitting}
                                        aria-busy={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm mr-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Submitting...
                                            </>
                                        ) : (
                                            'Submit request'
                                        )}
                                    </button>
                                </form>

                                <p className="text-muted text-center small mt-4 mb-0">
                                    We will respond within 30 days. For urgent requests or questions, email{' '}
                                    <a href="mailto:admin@mobicloud.ai">admin@mobicloud.ai</a>
                                </p>

                                <p className="text-center mt-3 mb-0">
                                    <Link to="/data-deletion-policy">User Data Deletion Policy</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LegalPageLayout>
    );
};

export default PrivacyRequestPage;
